import type { Container, Sprite } from 'pixi.js';
import { makeSprite } from '@/assets/resolver';
import type { PatternDef } from '@/patterns/dsl';
import type { BossDef } from './boss-base';

/**
 * B5 Warden — def, stage patterns and tuning (class logic in warden.ts).
 * Stage index doubles as the phase index for evolve layers:
 * 0 = ARMOR, 1 = CORE, 2 = RAGE. docs/02-bosses.md
 * ARMOR fires LOCKDOWN WALLS (find the hole) — not the Carrier's spiral.
 * CORE seals its hatch for every laser sweep, then VENTS (bonus damage).
 * RAGE is shielded except for short BREATH windows (glow → open).
 */
export const WARDEN: BossDef = {
  id: 'warden',
  nameKey: 'boss.warden',
  tier: 4,
  finale: true,
  baseHp: 1500,
  radius: 92,
  bodyKey: 'boss/warden/body',
  movement: 'orbit',
  contactDamage: 1,
  phases: [{ hpPct: 1.0, patterns: [] }],
  evolve: [
    {
      // Shockwave: a slow heavy ring rolls out once the hatch is blown
      tier: 2,
      fromPhase: 1,
      pattern: {
        type: 'radial', count: 14, interval: 4.8, spinDeg: 13,
        bullet: 'bullet/enemy/ring', speed: 120, radius: 10, damage: 2,
      },
    },
    {
      // Last stand: the core swells, then a wide aimed fan
      tier: 3,
      fromPhase: 2,
      pattern: {
        type: 'aimed', count: 5, spreadDeg: 44, interval: 3.0,
        telegraph: 0.5,
        bullet: 'bullet/enemy/bolt', speed: 290, radius: 10, damage: 1,
      },
    },
  ],
};

export type Stage = 'armor' | 'core' | 'rage';
export const STAGE_INDEX: Readonly<Record<Stage, number>> = { armor: 0, core: 1, rage: 2 };

export const TURRET_COUNT = 4;
export const TURRET_RADIUS = 24;
export const TURRET_ORBIT = 150;
export const TURRET_SPIN = 0.3; // rad/s
export const TURRET_FIRE = 2.0; // s between shots at tempo 1
export const TURRET_SHOT_SPEED = 270;
export const TURRET_HP_SHARE = 0.1; // each turret = 10% of max hp
/** The armor stage ends at this share of the bar (turrets carry the rest) */
export const CORE_HP_PCT = 1 - TURRET_COUNT * TURRET_HP_SHARE;
export const RAGE_AT = 0.3;
export const DRONES_AT = 0.45;
export const ORBIT = { rate: 0.18, rx: 110, ry: 80, yFreq: 1.4, follow: 0.7, lock: 1.5 };

/** Patterns per stage — signature unless `unlockTier` says otherwise. */
export const STAGE_PATTERNS: Readonly<Record<Stage, PatternDef[]>> = {
  armor: [
    {
      // Lockdown wall: a line of bolts abreast with a roaming hole
      type: 'wall', count: 13, spacing: 46, hole: 3, interval: 3.4, telegraph: 0.5,
      bullet: 'bullet/enemy/bolt', speed: 165, radius: 10, damage: 1,
    },
    {
      // Rail bolts: launch slow, then accelerate hard (docs/02 B3)
      type: 'aimed', count: 3, spreadDeg: 30, interval: 3.0, telegraph: 0.45, unlockTier: 2,
      bullet: 'bullet/enemy/bolt', speed: 80, radius: 10, damage: 1,
      motion: { accel: 380, maxSpeed: 330 },
    },
  ],
  core: [
    {
      // Core burst: the exposed core swells before the fan (X13)
      type: 'aimed', count: 5, spreadDeg: 36, interval: 2.2,
      telegraph: 0.45, burst: 2, burstGap: 0.3,
      bullet: 'bullet/enemy/bolt', speed: 280, radius: 10, damage: 1,
    },
    {
      type: 'radial', count: 18, interval: 2.6, spinDeg: 12,
      bullet: 'bullet/enemy/ring', speed: 150, radius: 10, damage: 2,
    },
  ],
  rage: [
    {
      type: 'spiral', arms: 3, interval: 0.1, angularSpeed: -95,
      bullet: 'bullet/enemy/ring', speed: 165, radius: 10, damage: 2,
    },
    {
      type: 'radial', count: 26, interval: 2.9,
      bullet: 'bullet/enemy/bolt', speed: 235, radius: 10, damage: 1,
    },
  ],
};

export const LASER = {
  interval: 6,
  charge: 0.8,
  active: 1.4,
  sweepSpeed: 1.5, // rad/s
  width: 24,
  length: 1500,
  /** Aim lead: the sweep starts this far (rad) behind the player */
  lead: 0.5,
  /** Extra hit reach past the beam edge (px) */
  hitPad: 8,
  /** Charge shimmer reach (px) — a short heat haze, not a full-length line */
  previewLen: 260,
  glow: 0xffc89e,
  core: 0xfff4e6,
};

/** Core/rage damage windows (docs/02 B1 — rewards the supershot). */
export const WINDOWS = {
  /** After a laser sweep the core vents: bonus damage for this long */
  ventTime: 1.8,
  ventMul: 1.6,
  /** Rage: shielded `breathEvery` s (glow in the last `breathWarn`), then
   *  open for `breathOpen` s with bonus damage — real seconds, any tier */
  breathEvery: 2.6,
  breathWarn: 0.6,
  breathOpen: 2.4,
  breathMul: 1.6,
  ventTint: 0xfff0d8,
  warnTint: 0xffe2a0,
};
export const RING = { start: 720, end: 360, shrinkTime: 22 };

export interface Turret {
  alive: boolean;
  hp: number;
  angle: number;
  x: number;
  y: number;
  fireTimer: number;
  sprite: Sprite;
}

/** Shoulder turrets (HP assigned by the boss after scaling). */
export function buildTurrets(view: Container, x: number, y: number): Turret[] {
  const turrets: Turret[] = [];
  for (let i = 0; i < TURRET_COUNT; i++) {
    const sprite = makeSprite('boss/warden/turret');
    view.addChild(sprite);
    turrets.push({
      alive: true, hp: 0,
      angle: (Math.PI / 2) * i + Math.PI / 4,
      x, y, fireTimer: 1 + i * 0.7, sprite,
    });
  }
  return turrets;
}

/** Nearest living turret within `reach` of a point (null when none). */
export function nearestTurret(
  turrets: ReadonlyArray<Turret>, x: number, y: number, reach: number,
): Turret | null {
  let best: Turret | null = null;
  let bestD = reach;
  for (const t of turrets) {
    if (!t.alive) continue;
    const d = Math.hypot(t.x - x, t.y - y);
    if (d < bestD) {
      bestD = d;
      best = t;
    }
  }
  return best;
}
